import { IconLayer } from '@deck.gl/layers';
import type { Device } from '@luma.gl/core';
import memoize from 'lodash/memoize';
import { DEVICE_PIXEL_RATIO } from 'src/lib/player/constants';
import { t } from 'src/lib/player/i18n';
import { PlayerColor } from 'src/lib/player/PlayerColor';
import type { LatLng, PoiType } from 'src/lib/player/store';
import { hasMouse } from 'src/lib/player/utils';
import SpriteManager, { type Sprite } from './SpriteManager';

const getPOISpriteManager = memoize(() => new SpriteManager());

export function createPOILayer({
  pois,
  selectedIds,
  ownLocation,
  gpuDevice,
  largeMode,
  onClick,
  onHover,
}: {
  pois: Array<PoiType>;
  selectedIds: Set<string>;
  ownLocation: LatLng | null;
  gpuDevice: Device | null;
  largeMode: boolean;
  onClick: (poi: PoiType) => void;
  onHover: (poi: null | PoiType) => void;
}): Array<IconLayer> {
  if (gpuDevice == null) return [];
  if (pois.length === 0 && ownLocation == null) return [];

  const spriteManager = getPOISpriteManager();
  const poiSprites: Array<Sprite> = pois.map((poi) => {
    return spriteManager.createPOISprite(poi, largeMode);
  });

  if (ownLocation != null) {
    poiSprites.push(
      spriteManager.createPOISprite(
        {
          type: 'POI',
          id: '___me',
          name: t('Me'),
          latlng: ownLocation,
          index: -1,
          eventIndex: -1,
          markerColor: PlayerColor.default(),
        },
        largeMode,
      ),
    );
  }

  return spriteManager.updateDirtySpritesheets(gpuDevice).map(
    (spritesheet) =>
      new IconLayer({
        id: `poi-icons-${spritesheet.index}`,
        iconMapping: spriteManager.sprites,
        iconAtlas: spritesheet.texture || undefined,
        data: poiSprites.filter((sprite) => sprite.sheetIndex === spritesheet.index),
        billboard: true,
        pickable: true,
        getSize: (sprite: Sprite) =>
          ((selectedIds.has(sprite._poi?.id ?? '') ? 1 : 0.5) * sprite.height) / DEVICE_PIXEL_RATIO,
        getIcon: (sprite: Sprite) => sprite.hashKey,
        // @ts-expect-error: ts(2322) Return type of getPosition is [number, number, number] but deck.gl expects [number, number]
        getPosition: (sprite: Sprite, { target }: { target: [number, number, number] }) => {
          target[0] = sprite.lng;
          target[1] = sprite.lat;
          target[2] = 2.0;
          return target;
        },
        // @ts-expect-error: ts(2322) Return type of getColor is [number, number, number, number] but deck.gl expects [number, number]
        getColor: (sprite: Sprite, { target }: { target: [number, number, number, number] }) => {
          target[0] = 255;
          target[1] = 255;
          target[2] = 255;
          target[3] = sprite.alpha * 255;
          return target;
        },
        onClick: ({ object }: { object?: Sprite | null }) => {
          const poi = object?._poi;
          if (poi != null && (poi.index ?? -1) >= 0) {
            onClick(poi);
          }
        },
        onHover: hasMouse()
          ? ({ object }: { object?: Sprite | null }) => {
              const poi = object?._poi;

              if (object == null) {
                onHover(null);
              } else if (poi != null && (poi.index ?? -1) >= 0) {
                onHover(poi);
              }
            }
          : null,
      }),
  );
}
